import { CheckCircleIcon, GiftIcon } from '@heroicons/react/24/outline';
import Tooltip from '@onbeef/components/tooltip';
import { Toast } from '@onbeefapp/constants';
import cx from 'classnames';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import Stars from '../../../../../packages/assets/images/stars.png';
import WallPaper from '../../../../../packages/assets/images/wallpaper.png';
import Star from '../../assets/images/star.png';
import HeaderBar from '../../components/Headers/HeaderBar';
import { LoyaltyPointReward } from '../../queries/loyalty/types';
import { useAuthStore } from '../../stores/login';
import { useMerchantStore } from '../../stores/merchant';
import { useCartLoyaltyPoints } from './hooks/useCartLoyaltyPoints';

export const PointRewards = () => {
  const merchant = useMerchantStore((state) => state.merchant);
  const user = useAuthStore((state) => state.user);
  const navigate = useNavigate();

  const { handleSelectReward, handleClearSelectedReward, selectedPointReward } =
    useCartLoyaltyPoints();

  const handleSelect = (reward: LoyaltyPointReward) => {
    handleSelectReward(reward);
    Toast.success(`Prêmio ${reward.name} selecionado!`);
    navigate('/');
  };

  const handleUnselectPointReward = (reward: LoyaltyPointReward) => {
    if (selectedPointReward?.id === reward.id) {
      handleClearSelectedReward();
      return;
    }
  };

  const handleOpenLoyaltyDetail = () => navigate('/loyalty/detail');

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const userLoyalty = user?.loyalty_programmes && user.loyalty_programmes[0];
  const userPoints = userLoyalty?.reward?.customer_points;

  const pointRewards = userLoyalty?.reward?.point_rewards;

  return (
    <div className="w-full h-full">
      <div className="bg-primary z-[-9] absolute w-full h-60 opacity-50" />

      <img
        src={Stars}
        alt="Imagem do clube"
        className="w-full h-60 z-[-8] object-cover absolute top-0 grayscale"
      />
      <img
        src={WallPaper}
        alt="Imagem do clube"
        className="w-full h-60 z-[-10] object-cover absolute top-0 grayscale"
      />

      <div className="w-full h-full space-y-2">
        <HeaderBar className="p-4 !mb-16" colorButton="bg-white"></HeaderBar>
        <div className="flex flex-col space-y-4 items-center">
          <div className="absolute top-5 flex flex-col space-y-2">
            <img
              src={Star}
              alt="Imagem do clube"
              className="h-14 w-14 z-[-8] self-center"
            />
            <div className="text-lg font-medium text-contrastText">
              Clube de fidelidade
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col p-5 gap-5 bg-gray-100 rounded-t-3xl">
        <div
          className="rounded-lg shadow-sm p-3 flex items-center justify-between cursor-pointer"
          onClick={handleOpenLoyaltyDetail}
        >
          <div>
            <p className="text-sm text-gray-500">Seus pontos disponíveis</p>
            <p className="text-3xl font-bold text-primary">
              {userPoints?.available || 0}
            </p>
          </div>
          <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
            <GiftIcon className="h-8 w-8 text-primary" />
          </div>
        </div>

        <div className="rounded-lg shadow-sm px-3 pb-5">
          <h2 className="text-lg font-semibold text-gray-800 mb-3">
            Prêmios disponíveis
          </h2>
          <div className="flex flex-col gap-4">
            {pointRewards?.map((reward) => {
              const isSelected = selectedPointReward?.id === reward.id;
              const canRedeem =
                userPoints && userPoints.available >= reward.quantity_points;

              return (
                <div
                  key={reward.id}
                  className={cx(
                    'flex items-center justify-between gap-2 p-4 border rounded-lg transition-all duration-200 hover:shadow-sm',
                    isSelected
                      ? 'border-green-500 bg-green-50'
                      : 'bg-white border-gray-100',
                  )}
                  onClick={() => handleUnselectPointReward(reward)}
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="flex-shrink-0">
                      <img
                        src={reward.file_url || merchant?.logo}
                        className={cx(
                          `object-cover h-16 w-16 rounded-md border-none`,
                        )}
                        loading="lazy"
                        alt={reward.name}
                      />
                    </div>

                    <div className="flex items-start flex-col gap-1 min-w-0 flex-1">
                      <Tooltip text={reward.name} containerClassName="w-full">
                        <h4 className="font-semibold text-gray-800 break-words line-clamp-3 w-full wrap-break-word">
                          {reward.name}
                        </h4>
                      </Tooltip>
                      <p className="text-sm text-gray-500 flex items-center">
                        <span className={canRedeem ? 'font-medium' : ''}>
                          {reward.quantity_points} pontos
                        </span>
                      </p>
                      <div className="h-1.5 w-full bg-gray-300 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-primary rounded-full"
                          style={{
                            width: `${Math.min(
                              100,
                              ((userPoints?.available ?? 0) /
                                reward.quantity_points) *
                                100,
                            )}%`,
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                    {isSelected ? (
                      <div className="flex flex-col items-end">
                        <CheckCircleIcon className="w-10 h-10 text-green-500" />
                        <span className="text-xs text-green-600 font-medium">
                          Selecionado
                        </span>
                      </div>
                    ) : (
                      <button
                        className={cx(
                          'bg-primary text-white rounded-lg px-4 py-2 font-medium text-sm hover:bg-primary/90 transition-all duration-200',
                          !canRedeem ? 'opacity-50 cursor-not-allowed' : '',
                        )}
                        type="button"
                        onClick={() => handleSelect(reward)}
                        disabled={!canRedeem}
                      >
                        Resgatar
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};
